import {
  customElement,
  DeesElement,
  type TemplateResult,
  html,
  css,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import type {
  IActionBarOptions,
  IActionBarResult,
  IActionBarQueueItem,
  IActionBarAction,
} from './actionbar.interfaces.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-actionbar': DeesActionbar;
  }
}

@customElement('dees-actionbar')
export class DeesActionbar extends DeesElement {
  // STATIC
  public static demoGroups = ['Feedback', 'Overlay'];
  public static demo = () => {
    const getActionbar = (e: Event) => {
      const button = e.currentTarget as HTMLElement;
      const container = button.closest('.demo-container');
      return container?.querySelector('dees-actionbar') as DeesActionbar | null;
    };

    const showActionBar = async (e: Event) => {
      const actionbar = getActionbar(e);
      if (!actionbar) return;
      const result = await actionbar.show({
        message: 'File changed externally. Reload?',
        type: 'warning',
        icon: 'lucide:alertTriangle',
        actions: [
          { id: 'reload', label: 'Reload', primary: true },
          { id: 'ignore', label: 'Ignore' },
        ],
        timeout: { duration: 5000, defaultActionId: 'reload' },
        dismissible: true,
      });
      console.log('Action bar result:', result);
    };

    const showErrorBar = async (e: Event) => {
      const actionbar = getActionbar(e);
      if (!actionbar) return;
      const result = await actionbar.show({
        message: 'Process failed with exit code 1',
        type: 'error',
        icon: 'lucide:xCircle',
        actions: [
          { id: 'retry', label: 'Retry', primary: true },
          { id: 'dismiss', label: 'Dismiss' },
        ],
        timeout: { duration: 10000, defaultActionId: 'dismiss' },
      });
      console.log('Error bar result:', result);
    };

    const showQuestionBar = async (e: Event) => {
      const actionbar = getActionbar(e);
      if (!actionbar) return;
      const result = await actionbar.show({
        message: 'Save changes before closing?',
        type: 'question',
        icon: 'lucide:helpCircle',
        actions: [
          { id: 'save', label: 'Save', primary: true },
          { id: 'discard', label: 'Discard' },
          { id: 'cancel', label: 'Cancel' },
        ],
      });
      console.log('Question bar result:', result);
    };

    return html`
      <style>
        .demo-container {
          display: flex;
          flex-direction: column;
          height: 300px;
          border: 1px solid #333;
          border-radius: 8px;
          overflow: hidden;
        }
        .demo-content {
          flex: 1;
          padding: 16px;
          display: flex;
          gap: 8px;
          align-items: flex-start;
        }
      </style>
      <div class="demo-container">
        <div class="demo-content">
          <dees-button @click=${showActionBar}>Warning</dees-button>
          <dees-button @click=${showErrorBar}>Error</dees-button>
          <dees-button @click=${showQuestionBar}>Question</dees-button>
        </div>
        <dees-actionbar></dees-actionbar>
      </div>
    `;
  };

  // Queue of pending action bars
  private queue: IActionBarQueueItem[] = [];

  // Current active bar state
  @state() accessor currentBar: IActionBarOptions | null = null;
  @state() accessor timeRemaining: number = 0;
  @state() accessor progressPercent: number = 100;
  @state() accessor isVisible: boolean = false;

  // Timeout handling
  private timeoutInterval: ReturnType<typeof setInterval> | null = null;
  private currentResolve: ((result: IActionBarResult) => void) | null = null;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        /* purple status accent (dark default, bright override below) */
        --actionbar-purple: hsl(270 70% 60%);
        display: block;
      }

      :host([gobright]) {
        --actionbar-purple: hsl(270 70% 50%);
      }

      .actionbar-item {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.2s ease-out;
        background: var(--dees-color-bg-tertiary);
        border-top: 1px solid var(--dees-color-border-default);
      }

      :host(.visible) .actionbar-item {
        grid-template-rows: 1fr;
      }

      .actionbar-content {
        overflow: hidden;
        min-height: 0;
        opacity: 0;
        transition: opacity 0.2s ease-out;
      }

      :host(.visible) .actionbar-content {
        opacity: 1;
      }

      .progress-bar {
        height: 3px;
        background: color-mix(in srgb, var(--dees-color-text-primary) 9%, var(--dees-color-bg-primary));
        overflow: hidden;
      }

      .progress-bar-fill {
        height: 100%;
        background: var(--dees-color-accent-primary);
        transition: width 0.1s linear;
      }

      .progress-bar-fill.warning {
        background: var(--dees-color-accent-warning);
      }

      .progress-bar-fill.error {
        background: var(--dees-color-text-error);
      }

      .progress-bar-fill.question {
        background: var(--actionbar-purple);
      }

      .content {
        display: flex;
        align-items: center;
        padding: 8px 12px;
        gap: 12px;
        min-height: 32px;
      }

      .message-section {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;
        min-width: 0;
      }

      .message-icon {
        flex-shrink: 0;
        color: var(--dees-color-text-muted);
      }

      .message-icon.info {
        color: var(--dees-color-accent-primary);
      }

      .message-icon.warning {
        color: var(--dees-color-text-warning);
      }

      .message-icon.error {
        color: var(--dees-color-text-error);
      }

      .message-icon.question {
        color: var(--actionbar-purple);
      }

      .message-text {
        font-size: 13px;
        color: var(--dees-color-text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .actions-section {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }

      .action-button {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        border: 1px solid transparent;
        transition: all 0.15s ease;
        white-space: nowrap;
      }

      .action-button.secondary {
        background: transparent;
        color: var(--dees-color-text-secondary);
        border-color: var(--dees-color-border-strong);
      }

      .action-button.secondary:hover {
        background: color-mix(in srgb, var(--dees-color-text-primary) 8%, var(--dees-color-bg-primary));
      }

      .action-button.primary {
        background: var(--dees-color-accent-primary);
        color: white;
      }

      .action-button.primary:hover {
        background: color-mix(in srgb, var(--dees-color-accent-primary) 88%, var(--dees-color-text-primary));
      }

      .action-button.primary.warning {
        background: color-mix(in srgb, var(--dees-color-accent-warning) 92%, var(--dees-color-text-primary));
      }

      .action-button.primary.warning:hover {
        background: color-mix(in srgb, var(--dees-color-accent-warning) 82%, var(--dees-color-text-primary));
      }

      .action-button.primary.error {
        background: var(--dees-color-text-error);
      }

      .action-button.primary.error:hover {
        background: color-mix(in srgb, var(--dees-color-accent-error) 88%, var(--dees-color-text-primary));
      }

      .action-button.primary.question {
        background: color-mix(in srgb, var(--actionbar-purple) 92%, var(--dees-color-text-primary));
      }

      .action-button.primary.question:hover {
        background: color-mix(in srgb, var(--actionbar-purple) 82%, var(--dees-color-text-primary));
      }

      .countdown {
        font-size: 11px;
        opacity: 0.8;
        margin-left: 2px;
      }

      .dismiss-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 4px;
        cursor: pointer;
        color: var(--dees-color-text-muted);
        transition: all 0.15s ease;
      }

      .dismiss-button:hover {
        background: var(--dees-color-border-default);
        color: var(--dees-color-text-secondary);
      }
    `,
  ];

  public render(): TemplateResult {
    const bar = this.currentBar;
    const type = bar?.type || 'info';
    const hasTimeout = bar?.timeout && this.timeRemaining > 0;

    // ALWAYS render wrapper - required for grid animation to work
    return html`
      <div class="actionbar-item">
        <div class="actionbar-content">
          ${bar ? html`
            ${hasTimeout ? html`
              <div class="progress-bar">
                <div
                  class="progress-bar-fill ${type}"
                  style="width: ${this.progressPercent}%"
                ></div>
              </div>
            ` : ''}
            <div class="content">
              <div class="message-section">
                ${bar.icon ? html`
                  <dees-icon
                    class="message-icon ${type}"
                    .icon=${bar.icon}
                    iconSize="16"
                  ></dees-icon>
                ` : ''}
                <span class="message-text">${bar.message}</span>
              </div>
              <div class="actions-section">
                ${bar.actions.map(action => this.renderActionButton(action, bar, hasTimeout))}
                ${bar.dismissible ? html`
                  <div
                    class="dismiss-button"
                    @click=${() => this.handleDismiss()}
                    title="Dismiss"
                  >
                    <dees-icon .icon=${'lucide:x'} iconSize="14"></dees-icon>
                  </div>
                ` : ''}
              </div>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  private renderActionButton(
    action: IActionBarAction,
    bar: IActionBarOptions,
    hasTimeout: boolean | undefined
  ): TemplateResult {
    const isPrimary = action.primary;
    const type = bar.type || 'info';
    const isDefaultAction = bar.timeout?.defaultActionId === action.id;
    const showCountdown = hasTimeout && isDefaultAction;
    const seconds = Math.ceil(this.timeRemaining / 1000);

    return html`
      <button
        class="action-button ${isPrimary ? `primary ${type}` : 'secondary'}"
        @click=${() => this.handleAction(action.id, false)}
      >
        ${action.icon ? html`
          <dees-icon .icon=${action.icon} iconSize="12"></dees-icon>
        ` : ''}
        <span>${action.label}</span>
        ${showCountdown ? html`
          <span class="countdown">(${seconds}s)</span>
        ` : ''}
      </button>
    `;
  }

  // ========== Public API ==========

  /**
   * Show an action bar with the given options.
   * Returns a promise that resolves when an action is taken.
   */
  public async show(options: IActionBarOptions): Promise<IActionBarResult> {
    return new Promise((resolve) => {
      // Add to queue
      this.queue.push({ options, resolve });

      // If no current bar, process queue
      if (!this.currentBar) {
        this.processQueue();
      }
    });
  }

  /**
   * Dismiss the current action bar without triggering any action.
   */
  public dismiss(): void {
    this.handleDismiss();
  }

  /**
   * Clear all pending action bars in the queue.
   */
  public clearQueue(): void {
    // Resolve all queued items with dismiss
    for (const item of this.queue) {
      item.resolve({ actionId: 'dismissed', timedOut: false });
    }
    this.queue = [];
  }

  // ========== Private Methods ==========

  /**
   * Hide the current actionbar with animation.
   * Removes visible class first to trigger CSS transition, then clears content after animation.
   */
  private async hideCurrentBar(): Promise<void> {
    // Remove visible class to start close animation
    this.classList.remove('visible');
    this.isVisible = false;

    // Wait for animation to complete (200ms transition + buffer)
    await new Promise(resolve => setTimeout(resolve, 220));

    // Now safe to clear content
    this.currentBar = null;
    this.currentResolve = null;
  }

  private async processQueue(): Promise<void> {
    if (this.queue.length === 0) {
      // Hide with animation - don't await, let it run async
      this.hideCurrentBar();
      return;
    }

    const item = this.queue.shift()!;
    this.currentBar = item.options;
    this.currentResolve = item.resolve;
    this.isVisible = true;

    // Wait for Lit render, then add class on next frame to trigger animation
    await this.updateComplete;
    requestAnimationFrame(() => {
      this.classList.add('visible');
    });

    // Setup timeout if configured
    if (item.options.timeout) {
      this.startTimeout(item.options.timeout.duration, item.options.timeout.defaultActionId);
    }
  }

  private startTimeout(duration: number, defaultActionId: string): void {
    this.timeRemaining = duration;
    this.progressPercent = 100;

    const startTime = Date.now();
    const updateInterval = 50; // Update every 50ms for smooth animation

    this.timeoutInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      this.timeRemaining = Math.max(0, duration - elapsed);
      this.progressPercent = (this.timeRemaining / duration) * 100;

      if (this.timeRemaining <= 0) {
        this.clearTimeoutInterval();
        this.handleAction(defaultActionId, true);
      }
    }, updateInterval);
  }

  private clearTimeoutInterval(): void {
    if (this.timeoutInterval) {
      clearInterval(this.timeoutInterval);
      this.timeoutInterval = null;
    }
  }

  private handleAction(actionId: string, timedOut: boolean): void {
    this.clearTimeoutInterval();

    if (this.currentResolve) {
      this.currentResolve({ actionId, timedOut });
    }

    // Process next item in queue
    this.processQueue();
  }

  private handleDismiss(): void {
    this.handleAction('dismissed', false);
  }

  public async disconnectedCallback(): Promise<void> {
    await super.disconnectedCallback();
    this.clearTimeoutInterval();
  }
}
